import { current } from '@reduxjs/toolkit';
import { isNullOrUndefined } from '../../utils/helpers';

const sanitizeError = (error: any): any => {
    if (!error) {
        return error;
    }

    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            stack: error.stack,
        };
    }

    if (typeof error === 'object') {
        return {
            ...(error?.name && { name: error.name }),
            ...(error?.message && { message: error.message }),
            ...(error?.stack && { stack: error.stack }),
            ...(error?.status && { status: error.status }),
            ...(error?.data !== undefined && { data: error.data }),
            ...(error?.error && { error: String(error.error) }),
        };
    }

    return { message: String(error) };
};

const sanitizeResponses = (responses: any): any => {
    if (!Array.isArray(responses)) {
        return [];
    }

    return responses.map((response: any) => {
        if (!response || typeof response !== 'object') {
            return response;
        }

        return {
            ...response,
            ...(response?.error !== undefined && { error: sanitizeError(response.error) }),
        };
    });
};

function setDashboardWidgets(state: any, action: any): void {
    const payloadClone = current(state);
    const prevWidgetIndex = payloadClone?.widgetList?.findIndex(
        (widget: any) => widget?.id === action?.payload?.widgetId
    );
    if (prevWidgetIndex === -1) {
        state.widgetList.push(action.payload.data);
    } else {
        state.widgetList[prevWidgetIndex] = { ...action.payload.data };
    }
}

function replaceDashboardWidgets(state: any, action: any): void {
    const payloadClone = current(state);
    const prevWidgetIndex = payloadClone?.widgetList?.findIndex(
        (widget: any) => widget?.id === action?.payload?.destinationWidgetId
    );
    const prevLayoutIndex = payloadClone?.layout?.findIndex(
        (widget: any) => widget?.i === action?.payload?.destinationWidgetId
    );
    if (prevWidgetIndex === -1) {
        state.widgetList.push(action.payload.data);
    } else {
        state.widgetList[prevWidgetIndex] = { ...action.payload.data };
    }

    if (prevLayoutIndex !== -1) {
        state.layout[prevLayoutIndex] = {
            ...payloadClone.layout[prevLayoutIndex],
            i: action?.payload?.widgetId,
        };
    }
    const selectedId = payloadClone?.dashboardWidgetIds?.findIndex(
        (item: any) => item === action?.payload?.destinationWidgetId
    );
    if (selectedId !== -1) {
        state.dashboardWidgetIds?.splice(selectedId, 1);
    }
}

function apiResponseHelper(state: any, action: any): void {
    const stateClone = current(state);
    const { widget, responses } = action.payload ?? {};
    const widgetId = widget?.id;
    const widgetType = widget?.widgetType?.id;
    const sanitizedResponses = sanitizeResponses(responses);

    const responseIndex = stateClone?.apiResponse?.findIndex((res: any) => res.widgetId === widgetId);

    if (responseIndex === -1) {
        state.apiResponse?.push({
            widgetId,
            type: widgetType,
            responses: sanitizedResponses,
        });
        // Clear loading state when first response is added
        delete state['loaders'][widgetId];
    } else if (!isNullOrUndefined(responseIndex)) {
        delete state['loaders'][widgetId];
        state.apiResponse[responseIndex] = { ...state.apiResponse[responseIndex], responses: sanitizedResponses };
    }
}

export { setDashboardWidgets, apiResponseHelper, replaceDashboardWidgets };
